'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Loader2 } from 'lucide-react';
import PostCard from './PostCard';

type FetchResult = { posts: any[]; total: number; totalPages: number };
type FetchAction = (page: number, limit: number) => Promise<FetchResult>;

type Tier = {
  fetchAction: FetchAction;
};

export default function TieredInfinitePostList({
  tiers,
  initialPosts,
  initialState,
  limit = 12,
  className,
}: {
  tiers: Tier[];
  initialPosts: any[];
  initialState: {
    tierPages: number[];
    exhausted: boolean[];
    buffer: any[];
  };
  limit?: number;
  className?: string;
}) {
  const [posts, setPosts] = useState<any[]>(initialPosts);
  const [loading, setLoading] = useState(false);

  const tierPagesRef = useRef<number[]>(initialState.tierPages);
  const exhaustedRef = useRef<boolean[]>(initialState.exhausted);
  const bufferRef = useRef<any[]>(initialState.buffer || []);

  const seenIdsRef = useRef<Set<string>>(
    new Set([
      ...initialPosts.map(p => String(p.id)),
      ...(initialState.buffer || []).map(p => String(p.id)),
    ]),
  );

  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: '200px',
  });

  const hasMore = useMemo(() => {
    if (bufferRef.current.length > 0) return true;
    return exhaustedRef.current.some(v => !v);
  }, [posts.length]);

  useEffect(() => {
    if (inView && hasMore && !loading) {
      void loadMore();
    }
  }, [inView, hasMore, loading]);

  useEffect(() => {
    if (posts.length < limit && hasMore && !loading) {
      void loadMore();
    }
  }, []);

  const loadMore = async () => {
    if (loading) return;
    if (tiers.length === 0) return;

    setLoading(true);
    try {
      const batch: any[] = [];

      while (batch.length < limit && bufferRef.current.length > 0) {
        batch.push(bufferRef.current.shift());
      }

      for (let i = 0; i < tiers.length && batch.length < limit; i++) {
        if (exhaustedRef.current[i]) continue;

        let safety = 0;
        while (batch.length < limit && !exhaustedRef.current[i] && safety < 8) {
          safety++;
          const page = tierPagesRef.current[i] || 1;
          const res = await tiers[i].fetchAction(page, limit);
          tierPagesRef.current[i] = page + 1;

          const noPosts = !res.posts || res.posts.length === 0;
          const endOfTier =
            (Number.isFinite(res.totalPages) && page >= res.totalPages) ||
            (Number.isFinite(res.total) && page * limit >= res.total);

          if (noPosts || endOfTier) {
            exhaustedRef.current[i] = true;
          }

          const uniqueNewPosts = (res.posts || []).filter((p: any) => {
            const id = String(p?.id ?? '');
            if (!id) return false;
            if (seenIdsRef.current.has(id)) return false;
            seenIdsRef.current.add(id);
            return true;
          });

          if (uniqueNewPosts.length === 0) {
            if (noPosts || endOfTier) break;
            continue;
          }

          const remaining = limit - batch.length;
          const toAdd = uniqueNewPosts.slice(0, remaining);
          const overflow = uniqueNewPosts.slice(remaining);

          batch.push(...toAdd);
          if (overflow.length > 0) {
            bufferRef.current.push(...overflow);
          }
        }
      }

      if (batch.length > 0) {
        setPosts(prev => [...prev, ...batch]);

        // Analytics (GA4: View Item List) - Track related article impressions
        import('@next/third-parties/google').then(({ sendGAEvent }) => {
          const gaItems = batch.map((p: any) => ({
            item_id: p.id,
            item_name: p.title,
            item_category: p.category_name || 'Related Article',
          }));

          sendGAEvent('event', 'view_item_list', {
            item_list_id: 'related_feed',
            item_list_name: 'Related Articles Feed',
            items: gaItems,
          });
        }).catch(() => {
          // Ignore import failure if GA is fully blocked by client
        });
      }
    } catch {
      exhaustedRef.current = exhaustedRef.current.map(() => true);
    } finally {
      setLoading(false);
    }
  };

  if (tiers.length === 0) return null;

  return (
    <div className="w-full">
      <div className={className || 'flex flex-col gap-10 w-full'}>
        {posts.map((post, index) => {
          return (
            <React.Fragment key={`${post.id}-${index}`}>
              <PostCard post={post} />
            </React.Fragment>
          );
        })}
      </div>

      {hasMore && (
        <div ref={ref} className="flex justify-center py-10">
          {loading ? (
            <div className="flex items-center gap-2 text-[var(--color-primary)]">
              <Loader2 className="animate-spin" size={22} />
              <span className="font-medium">Loading more…</span>
            </div>
          ) : (
            <div className="h-4 w-full" />
          )}
        </div>
      )}

      {!hasMore && posts.length > 0 && (
        <div className="text-center py-10 text-[var(--color-text-muted)] border-t border-[var(--color-border)] mt-8">
          You&apos;ve reached the end of the list.
        </div>
      )}
    </div>
  );
}
